
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ProjectSlide {
  id: string;
  project_id: string;
  user_id: string;
  slide_type: string;
  title?: string;
  content?: string;
  image_storage_path?: string;
  video_embed_url?: string;
  background_color?: string;
  text_color?: string;
  created_at: string;
  updated_at: string;
}

export const useProjectSlides = (projectId: string) => {
  return useQuery({
    queryKey: ['project-slides', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_slides')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching project slides:', error);
        throw error;
      }

      return data as ProjectSlide[];
    },
    enabled: !!projectId,
  });
};

export const useCreateProjectSlide = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slideData: {
      project_id: string;
      slide_type?: string;
      title?: string;
      content?: string;
      image_storage_path?: string;
      video_embed_url?: string;
      background_color?: string;
      text_color?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('project_slides')
        .insert([
          {
            ...slideData,
            user_id: user.id,
          },
        ])
        .select()
        .single();

      if (error) {
        console.error('Error creating project slide:', error);
        throw error;
      }

      return data as ProjectSlide;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['project-slides', data.project_id] });
      toast.success('Slide created successfully!');
    },
    onError: (error) => {
      console.error('Failed to create slide:', error);
      toast.error('Failed to create slide');
    },
  });
};
